import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const F = "'Montserrat',sans-serif";

/**
 * Page controls for a server-paged list.
 *
 * Shared rather than per-page: Companies, Leads and Quality all read fixed-size
 * pages from the API, and a list that reports its range differently on each screen
 * reads as three different products.
 *
 * `total` is the count of everything matching the filters, not the rows on screen —
 * that distinction is the whole point of paging, so the caller must pass the API's
 * total and not `items.length`.
 */
export default function Pagination({ total, offset, limit, onChange, disabled }) {
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const page      = Math.floor(offset / limit) + 1;
  const first     = total === 0 ? 0 : offset + 1;
  const last      = Math.min(offset + limit, total);

  // One page of results needs no controls, but the count is still worth showing.
  const go = (p) => onChange((Math.min(Math.max(p, 1), pageCount) - 1) * limit);

  const btn = (enabled) => ({
    display: "flex", alignItems: "center", gap: 3,
    padding: "4px 9px", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600,
    border: "1px solid #D8DADC", background: "#fff",
    color: enabled ? "#3F3F46" : "#D8DADC",
    cursor: enabled ? "pointer" : "default",
  });

  // Current page, plus up to two either side, with the ends always reachable.
  const windowed = () => {
    const around = [page - 1, page, page + 1].filter((p) => p > 1 && p < pageCount);
    const pages  = [...new Set([1, ...around, pageCount])].filter((p) => p >= 1 && p <= pageCount);
    const out = [];
    pages.forEach((p, i) => {
      if (i > 0 && p - pages[i - 1] > 1) out.push("gap");
      out.push(p);
    });
    return out;
  };

  const canPrev = page > 1 && !disabled;
  const canNext = page < pageCount && !disabled;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 12, padding: "8px 14px", borderTop: "1px solid #D8DADC", background: "#fff",
    }}>
      <span style={{ fontSize: 10, color: "#818084", fontFamily: F }}>
        {total === 0
          ? "No companies match these filters"
          : <>Showing <b style={{ color: "#26262A" }}>{first}–{last}</b> of <b style={{ color: "#26262A" }}>{total}</b></>}
      </span>

      {pageCount > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button style={btn(canPrev)} disabled={!canPrev} onClick={() => go(page - 1)}>
            <ChevronLeft size={12} /> Prev
          </button>

          {windowed().map((p, i) =>
            p === "gap" ? (
              <span key={`gap${i}`} style={{ fontSize: 11, color: "#818084", padding: "0 2px" }}>…</span>
            ) : (
              <button
                key={p}
                disabled={disabled}
                onClick={() => go(p)}
                style={{
                  minWidth: 26, padding: "4px 7px", borderRadius: 6, fontFamily: F,
                  fontSize: 11, fontWeight: p === page ? 700 : 600, cursor: disabled ? "default" : "pointer",
                  border: `1px solid ${p === page ? "#E8431A" : "#D8DADC"}`,
                  background: p === page ? "#FEF1ED" : "#fff",
                  color: p === page ? "#E8431A" : "#3F3F46",
                }}>
                {p}
              </button>
            )
          )}

          <button style={btn(canNext)} disabled={!canNext} onClick={() => go(page + 1)}>
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
